/**
 * TeamMemberSelect (Seth 2026-06-26) — the ONE control for "pick a person" anywhere
 * in Pegasus (salesperson, installer, assignee, …). It reads the LIVE active roster
 * (useAllActiveTechs → mirror_employee where inactive = false, pseudo-employees
 * excluded), so the moment a team member is hired or let go in the source data the
 * dropdown reflects it — no hardcoded name lists to maintain anywhere (Seth: "as we
 * add/remove team members it should auto add/remove — for all of Pegasus").
 *
 * Reuse this instead of a free-text name box: free text is the ESC habit that breeds
 * "JON" / "Jon" / "John S" for one human, which can't be joined or reported on.
 *
 * Stores the stable empno; hands back the display name too, for places that persist a
 * name (e.g. the invoice's comfort_advisor text column).
 */
import { useMemo } from "react";
import { cn } from "@/lib/cn";
import { useAllActiveTechs } from "@/features/board/api";

type Person = { empno: string; empname: string | null; firstname: string | null; lastname: string | null; dept: string | null };

/** First + last when we have them, else the ESC empname, else the raw empno. */
export function memberDisplayName(t: Pick<Person, "firstname" | "lastname" | "empname" | "empno">): string {
  const full = [t.firstname, t.lastname].filter(Boolean).join(" ").trim();
  return full || t.empname || t.empno;
}

/**
 * Resolve a stored empno to a display name off the live roster. Returns a lookup
 * function so a component can resolve many. Falls back to the raw stored value so
 * legacy free-text names (older Pegasus jobs) still render as-is instead of blank.
 */
export function useMemberName() {
  const { data: all = [] } = useAllActiveTechs();
  return useMemo(() => {
    const byEmpno = new Map(all.map((m) => [m.empno, memberDisplayName(m as Person)]));
    return (value: string | null | undefined): string | null =>
      value ? (byEmpno.get(value) ?? value) : null;
  }, [all]);
}

const BASE =
  "w-full rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:cursor-not-allowed disabled:opacity-60";

export function TeamMemberSelect({
  value,
  onChange,
  includeBlank = true,
  blankLabel = "—",
  dept,
  className,
  disabled = false,
}: {
  /** selected empno (stable key), or null for none */
  value: string | null;
  /** fires with the empno AND the resolved display name (null when cleared) */
  onChange: (empno: string | null, name: string | null) => void;
  /** show an empty option (for optional fields like salesperson) */
  includeBlank?: boolean;
  blankLabel?: string;
  /** optionally scope to one department code (e.g. "03" techs); default = whole team */
  dept?: string;
  className?: string;
  disabled?: boolean;
}) {
  const { data: all = [] } = useAllActiveTechs();

  const members = useMemo(() => {
    const list = (dept ? all.filter((m) => m.dept === dept) : all) as Person[];
    return [...list].sort((a, b) => memberDisplayName(a).localeCompare(memberDisplayName(b)));
  }, [all, dept]);

  return (
    <select
      className={cn(BASE, className)}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => {
        const empno = e.target.value || null;
        const m = members.find((x) => x.empno === empno) ?? null;
        onChange(empno, m ? memberDisplayName(m) : null);
      }}
    >
      {includeBlank && <option value="">{blankLabel}</option>}
      {members.map((m) => (
        <option key={m.empno} value={m.empno}>
          {memberDisplayName(m)}
        </option>
      ))}
    </select>
  );
}
